import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getCityById, createCity, updateCity, getCountries, getZones } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import FloatingInput from "../components/UI/FloatingInput";
import SelectBox from "../components/UI/SelectBox";
import { notify } from "../utils/notify";
import PageHeader from "../components/UI/PageHeader";
import EntityTranslationsButton from "../components/UI/EntityTranslationsButton";
import { useTranslation } from "../context/I18nContext";

export default function AddEditCity() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("cities");

  const [countries, setCountries] = useState([]);
  const [zones, setZones] = useState([]);
  const [form, setForm] = useState({
    name: "",
    ascii_name: "",
    country_id: "",
    zone_id: "",
    latitude: "",
    longitude: "",
    population: "",
    timezone: "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    getCountries(token).then(setCountries).catch(() => notify.error(t("notify_load_countries_failed", "Failed to load countries")));

    if (id) {
      getCityById(token, id).then((c) => {
        setForm({
          name: c.name || "",
          ascii_name: c.ascii_name || "",
          country_id: c.country_id ? String(c.country_id) : "",
          zone_id: c.zone_id ? String(c.zone_id) : "",
          latitude: c.latitude ?? "",
          longitude: c.longitude ?? "",
          population: c.population ?? "",
          timezone: c.timezone || "",
        });
      }).catch(() => notify.error(t("notify_load_failed", "Failed to load city")));
    }
  }, [id, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (form.country_id) {
      getZones(token, { country_id: form.country_id }).then(setZones).catch(() => setZones([]));
    } else {
      setZones([]);
    }
  }, [form.country_id, token]);

  const countryOptions = countries.map((c) => ({ value: String(c.id), label: c.name }));
  const zoneOptions = [{ value: "", label: t("zone_none_option", "No zone (direct to country)") }, ...zones.map((z) => ({ value: String(z.id), label: z.name }))];

  const setField = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = async () => {
    setErrors({});
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = t("validation_name_required", "City name is required");
    if (!form.country_id) newErrors.country_id = t("validation_country_required", "Country is required");

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      notify.error(tCommon("validation_fill_required_fields", "Please fill in the required fields correctly"));
      return;
    }

    setLoading(true);
    const payload = {
      name: form.name.trim(),
      ascii_name: form.ascii_name.trim(),
      country_id: Number(form.country_id),
      zone_id: form.zone_id ? Number(form.zone_id) : 0,
      latitude: form.latitude ? Number(form.latitude) : 0,
      longitude: form.longitude ? Number(form.longitude) : 0,
      population: form.population ? Number(form.population) : 0,
      timezone: form.timezone.trim(),
    };

    try {
      if (id) {
        await updateCity(token, id, payload);
        notify.success(t("notify_updated", "City updated successfully"));
      } else {
        await createCity(token, payload);
        notify.success(t("notify_created", "City created successfully"));
      }
      navigate("/cities");
    } catch (err) {
      notify.error(err.data?.error || err.message || tCommon("error_generic", "Something went wrong"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto">
      <PageHeader className="flex items-center gap-4 mb-8 border-b border-gray-100 pb-6">
        <button onClick={() => navigate("/cities")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
          <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
        </button>
        <div>
          <h2 className="text-2xl font-black text-gray-800 tracking-tight">{id ? t("edit_title", "Edit City") : t("add_title", "Add New City")}</h2>
          <p className="text-gray-400 text-sm italic font-medium">{t("form_subtitle", "Define a city within a country and optional zone")}</p>
        </div>
      </PageHeader>

      <div className="bg-white p-6 md:p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
        <div className="space-y-6">
          <div>
            <SelectBox
              label={t("field_country_label", "Country")}
              options={countryOptions}
              value={form.country_id}
              onChange={(val) => {
                setForm((prev) => ({ ...prev, country_id: val, zone_id: "" }));
                if (errors.country_id) setErrors((prev) => ({ ...prev, country_id: undefined }));
              }}
            />
            {errors.country_id && <p className="text-[10px] text-red-500 font-bold mt-1 ms-2 uppercase tracking-tight">{errors.country_id}</p>}
          </div>

          <SelectBox
            label={t("field_zone_label", "Zone (optional)")}
            options={zoneOptions}
            value={form.zone_id}
            onChange={(val) => setForm((prev) => ({ ...prev, zone_id: val }))}
          />

          <div className="flex items-end gap-3">
            <div className="flex-grow">
              <FloatingInput label={t("field_name_label", "City Name")} value={form.name} onChange={setField("name")} error={errors.name} icon={ICONS.Cities} />
            </div>
            <EntityTranslationsButton entityType="city" entityId={id} defaultName={form.name} />
          </div>
          <FloatingInput label={t("field_ascii_name_label", "ASCII Name")} value={form.ascii_name} onChange={setField("ascii_name")} />

          <div className="grid grid-cols-2 gap-4">
            <FloatingInput label={t("field_latitude_label", "Latitude")} type="number" value={form.latitude} onChange={setField("latitude")} />
            <FloatingInput label={t("field_longitude_label", "Longitude")} type="number" value={form.longitude} onChange={setField("longitude")} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FloatingInput label={t("field_population_label", "Population")} type="number" value={form.population} onChange={setField("population")} />
            <FloatingInput label={t("field_timezone_label", "Timezone")} value={form.timezone} onChange={setField("timezone")} />
          </div>

          <div className="flex gap-4 pt-6 border-t border-gray-50">
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={loading}
              className="flex-grow shadow-lg shadow-blue-500/10"
              icon={id ? ICONS.Save : ICONS.Plus}
            >
              {id ? t("btn_update_city", "Update City") : t("btn_create_city", "Create City")}
            </Button>
            <Button variant="secondary" onClick={() => navigate("/cities")}>{tCommon("btn_cancel", "Cancel")}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
